// Tiny deep-merge for plain objects. Right side wins on leaf values.
// Used at the data-access layer to merge a stored partial structure into a
// typed default so consumers always get the full expected shape.
//
// Limitations (intentional — keep it simple):
// - Arrays are NOT merged. The right side replaces the left side entirely.
// - Only handles plain objects. Class instances, Maps, Sets, etc. fall through
//   to the right side.
// - No cycle detection. Don't pass it cyclic data.

export function deepMerge<T>(left: T, right: Partial<T> | undefined | null): T {
    if (right === undefined || right === null) return left;

    if (!isPlainObject(left) || !isPlainObject(right)) {
        return right as T;
    }

    const result: any = { ...left };
    for (const key of Object.keys(right)) {
        const lv = (left as any)[key];
        const rv = (right as any)[key];
        if (isPlainObject(lv) && isPlainObject(rv)) {
            result[key] = deepMerge(lv, rv);
        } else if (rv !== undefined) {
            result[key] = rv;
        }
    }
    return result as T;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v) && Object.getPrototypeOf(v) === Object.prototype;
}
